import { useId } from 'react';

/**
 * Choosing a colour: a row of named swatches, and **Custom…** for any other, which opens
 * the browser's own picker. Used by the Idea Board, and later by the Atlas's categories,
 * regions and drawings. `value` is always "#rrggbb".
 */

export interface Swatch {
  readonly hex: string;
  readonly name: string;
}

/** Soft colours for notes, cards and shapes; the first three are the desktop's defaults for them. */
export const BOARD_SWATCHES: readonly Swatch[] = [
  { hex: '#F5F2EE', name: 'Paper' },
  { hex: '#E9E1F5', name: 'Lavender' },
  { hex: '#DCD5C9', name: 'Stone' },
  { hex: '#FFFFFF', name: 'White' },
  { hex: '#FFF1A8', name: 'Yellow' },
  { hex: '#FFD9B3', name: 'Peach' },
  { hex: '#F9C9C9', name: 'Rose' },
  { hex: '#CDE8C3', name: 'Sage' },
  { hex: '#C6E2F2', name: 'Sky' },
  { hex: '#C9C3E8', name: 'Iris' },
  { hex: '#B9A891', name: 'Leather' },
  { hex: '#8C8C96', name: 'Slate' },
];

/** Strong colours for map categories and regions, the sample realm's among them. */
export const MAP_SWATCHES: readonly Swatch[] = [
  { hex: '#3388CC', name: 'Blue' },
  { hex: '#5B6FB5', name: 'Indigo' },
  { hex: '#8E7CC3', name: 'Violet' },
  { hex: '#7A3E9D', name: 'Purple' },
  { hex: '#B23A2F', name: 'Red' },
  { hex: '#E08E45', name: 'Orange' },
  { hex: '#C9A227', name: 'Gold' },
  { hex: '#6D8B74', name: 'Sage' },
  { hex: '#2E7D4F', name: 'Green' },
  { hex: '#A67C52', name: 'Brown' },
  { hex: '#5E5448', name: 'Umber' },
  { hex: '#3E3A45', name: 'Charcoal' },
];

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export function ColorField({ label, value, onChange, swatches = BOARD_SWATCHES }: { label: string; value: string; onChange: (hex: string) => void; swatches?: readonly Swatch[] }) {
  const id = useId();
  const custom = !swatches.some((s) => same(s.hex, value));
  return (
    <fieldset className="color-field">
      <legend>{label}</legend>
      <div className="color-swatches" role="radiogroup" aria-label={label}>
        {swatches.map((s) => (
          <button
            key={s.hex}
            type="button"
            role="radio"
            aria-checked={same(s.hex, value)}
            aria-label={s.name}
            title={s.name}
            className="color-swatch"
            style={{ background: s.hex }}
            onClick={() => onChange(s.hex)}
          />
        ))}
        <label className={custom ? 'color-custom chosen' : 'color-custom'} htmlFor={id} title="Any other colour">
          <span className="color-swatch" style={{ background: custom ? value : undefined }} aria-hidden="true" />
          Custom…
          <input id={id} type="color" value={value} onChange={(e) => onChange(e.currentTarget.value)} />
        </label>
      </div>
    </fieldset>
  );
}
